import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { submitContact, apiErrorMessage } from "../../lib/api";
import { PRICING_PLANS } from "../../config/site";

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your name (at least 2 characters).").max(80),
  email: z.string().trim().email("Please enter a valid email address."),
  mobile: z.string().trim().regex(/^\+?[0-9][0-9\s-]{7,17}$/, "Please enter a valid mobile number.")
    .refine((v) => { const d = v.replace(/\D/g, "").length; return d >= 10 && d <= 15; }, "Mobile number should have 10–15 digits."),
  subject: z.string().trim().min(3, "Please add a subject.").max(120),
  message: z.string().trim().min(10, "Message should be at least 10 characters.").max(2000),
});

const FIELDS = [
  { name: "name", label: "Name", autoComplete: "name" },
  { name: "email", label: "Email", type: "email", autoComplete: "email" },
  { name: "mobile", label: "Mobile Number", type: "tel", autoComplete: "tel", placeholder: "+91 98765 43210" },
  { name: "subject", label: "Subject" },
];

const inputCls = "mt-2 w-full rounded-lg border bg-[#07080C] px-4 py-3 text-base text-slate-100 placeholder:text-slate-600 transition-colors focus:outline-none focus:border-[#FF5500]/70";

const Field = ({ f, register, error, className = "" }) => (
  <div className={className}>
    <label htmlFor={`contact-${f.name}`} className="text-sm font-medium text-slate-300">{f.label}</label>
    {f.textarea
      ? <textarea id={`contact-${f.name}`} rows={6} data-testid={`contact-input-${f.name}`} aria-invalid={!!error} aria-describedby={error ? `err-${f.name}` : undefined} className={`${inputCls} resize-y ${error ? "border-red-500/70" : "border-white/10"}`} {...register(f.name)} />
      : <input id={`contact-${f.name}`} type={f.type || "text"} autoComplete={f.autoComplete} placeholder={f.placeholder} data-testid={`contact-input-${f.name}`} aria-invalid={!!error} aria-describedby={error ? `err-${f.name}` : undefined} className={`${inputCls} ${error ? "border-red-500/70" : "border-white/10"}`} {...register(f.name)} />}
    {error && <p id={`err-${f.name}`} role="alert" data-testid={`contact-error-${f.name}`} className="mt-2 text-sm text-red-400">{error.message}</p>}
  </div>
);

const SuccessCard = ({ onReset }) => (
  <div data-testid="contact-success" role="status" className="rounded-2xl border border-[#00E676]/30 bg-[#00E676]/5 p-10">
    <CheckCircle2 className="h-10 w-10 text-[#00E676]" aria-hidden="true" />
    <h2 className="mt-5 font-display text-2xl font-bold text-slate-50">Message received.</h2>
    <p className="mt-2 text-slate-400">Thank you for reaching out. Our team will get back to you soon.</p>
    <button type="button" data-testid="contact-send-another" onClick={onReset} className="mt-8 min-h-[44px] rounded-lg border border-white/15 px-5 text-sm font-semibold text-slate-100 hover:bg-white/5 focus-ring">Send another message</button>
  </div>
);

export const ContactForm = () => {
  const [params] = useSearchParams();
  const plan = PRICING_PLANS.find((p) => p.id === params.get("plan"));
  const accountHelp = params.get("topic") === "account";
  const [done, setDone] = useState(false);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", mobile: "", subject: accountHelp ? "Account help: I forgot my login details" : plan ? `Interested in ${plan.name}` : "", message: "" },
  });

  const onSubmit = async (values) => {
    try {
      await submitContact({ ...values, category: accountHelp ? "ACCOUNT_HELP" : "GENERAL" });
      toast.success("Message sent. We'll be in touch.");
      reset();
      setDone(true);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  if (done) return <SuccessCard onReset={() => setDone(false)} />;

  return (
    <form data-testid="contact-form" noValidate onSubmit={handleSubmit(onSubmit)} className="grid gap-6 rounded-2xl border border-white/10 bg-[#0F111A] p-6 sm:grid-cols-2 sm:p-10">
      {accountHelp && <p data-testid="contact-account-help-note" className="rounded-lg border border-[#FF5500]/30 bg-[#FF5500]/5 px-4 py-3 text-sm text-slate-300 sm:col-span-2">Forgot your login details? Use the mobile number and email on your CARLINK account. Our team will verify you and send a temporary password.</p>}
      {FIELDS.map((f) => <Field key={f.name} f={f} register={register} error={errors[f.name]} />)}
      <Field f={{ name: "message", label: "Message", textarea: true }} register={register} error={errors.message} className="sm:col-span-2" />
      <button type="submit" data-testid="contact-submit-button" disabled={isSubmitting}
        className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg bg-[#FF5500] px-8 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#FF6B1A] disabled:opacity-60 focus-ring sm:col-span-2 sm:justify-self-start">
        {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {isSubmitting ? "Sending…" : "Send message"}
      </button>
    </form>
  );
};
